import React from 'react';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import MetricChartWidget from 'components/widgets/charts/MetricChartWidget';
import { TimeSeriesPoint, TimeSeriesSeries } from 'components/widgets/charts/TimeSeriesChart';
import { Deployment, DynoType } from './monitoringTypes';
import { useDynoMetrics, DynoInstancePoint, TimeRange } from './useDynoMetrics';

// Soft colors for when multiple dynos of one type are running
const SERIES_COLORS = [
    'var(--primary-color)',
    'var(--general-blue)',
    'var(--general-purple)',
    'var(--positive-green)',
    'var(--general-red)',
];

// Basic/Standard-1x dynos share up to 4 vcpus with their neighbours,
// used as a rule-of-thumb ceiling for the load average chart
const DYNO_CORE_COUNT = 4;

// General rule of thumb for when a response time starts to feel slow
const RESPONSE_TIME_GUIDE_MS = 1000;

// Builds an x-axis formatter that includes the day once the
// selected range spans more than a single day
const makeXAxisFormatter = (range: TimeRange) => {
    const spanHours = (range.to.getTime() - range.from.getTime()) / (60 * 60 * 1000);
    return (value: string) => dayjs(value).format(spanHours > 24 ? 'DD-MM HH:mm' : 'HH:mm');
};

// Pivots instance rows into one series per dyno id
function pivotInstancesByDyno(instances: DynoInstancePoint[], valueKey: keyof DynoInstancePoint) {
    const pointsByTimestamp: Record<string, TimeSeriesPoint> = {};
    const dynoIds: string[] = [];

    // Group every row under its timestamp and dyno id
    instances.forEach(row => {
        if (!dynoIds.includes(row.dyno_id)) {
            dynoIds.push(row.dyno_id);
        }
        if (!pointsByTimestamp[row.timestamp]) {
            pointsByTimestamp[row.timestamp] = { timestamp: row.timestamp };
        }
        pointsByTimestamp[row.timestamp][row.dyno_id] = row[valueKey] as number | null;
    });

    // Sort the points chronologically for the chart
    const points = Object.values(pointsByTimestamp).sort((a, b) => (a.timestamp > b.timestamp ? 1 : -1));

    // Build one series definition per dyno id
    const series: TimeSeriesSeries[] = dynoIds.map((dynoId, index) => ({
        key: dynoId,
        label: dynoId.length > 8 ? `dyno ${dynoId.slice(-6)}` : dynoId,
        color: SERIES_COLORS[index % SERIES_COLORS.length],
    }));

    return { points, series };
}

// Finds the memory quota from the most recent sample carrying one
function findMemoryQuota(instances: DynoInstancePoint[]) {
    return [...instances].reverse().find(row => row.memory_quota_mb)?.memory_quota_mb ?? undefined;
}

interface DynoMonitoringSectionProps {
    dynoType: DynoType;
    range: TimeRange;
    viewerToken?: string;
    deployment?: Deployment;
}

// Renders the monitoring charts of the selected dyno type and
// deployment, memory and load average for every type plus response
// time for web since the router only measures http traffic there
const DynoMonitoringSection: React.FC<DynoMonitoringSectionProps> = ({ dynoType, range, viewerToken, deployment }) => {
    const { t } = useTranslation();
    const { dynoMetrics, loading } = useDynoMetrics(dynoType, range, viewerToken, deployment);
    const formatXAxis = makeXAxisFormatter(range);

    // Only the web dyno serves requests and has response times
    const isWebDyno = dynoType === 'web';

    // Pivot the raw instance rows into memory and load series
    const memory = pivotInstancesByDyno(dynoMetrics.instances, 'memory_total_mb');
    const load = pivotInstancesByDyno(dynoMetrics.instances, 'load_avg_1m');
    const memoryQuota = findMemoryQuota(dynoMetrics.instances);

    // Build the response time series
    const responseTimePoints: TimeSeriesPoint[] = dynoMetrics.response_time.map(row => ({
        timestamp: row.timestamp,
        response_time_avg_ms: row.response_time_avg_ms,
    }));

    // Show which deployment the server answered for in the subtitle
    const deploymentLabel = dynoMetrics.deployment ? t(`dashboard.monitoring.deployment.${dynoMetrics.deployment}`) : '';
    const dynoLabel = t(`dashboard.monitoring.dyno_type.${dynoType}`);
    const subtitle = deploymentLabel ? `${deploymentLabel} / ${dynoLabel}` : dynoLabel;

    return (
        <div className='admin-charts-grid-3'>
            <MetricChartWidget
                title={t('dashboard.monitoring.chart.memory_title')}
                subtitle={subtitle}
                data={memory.points}
                series={memory.series}
                loading={loading}
                xFormatter={formatXAxis}
                yDomainMax={memoryQuota}
                referenceLine={memoryQuota ? { value: memoryQuota, label: t('dashboard.monitoring.chart.memory_quota_label') } : undefined}
            />
            <MetricChartWidget
                title={t('dashboard.monitoring.chart.load_title')}
                subtitle={subtitle}
                data={load.points}
                series={load.series}
                loading={loading}
                xFormatter={formatXAxis}
                yDomainMax={DYNO_CORE_COUNT}
                referenceLine={{ value: DYNO_CORE_COUNT, label: t('dashboard.monitoring.chart.load_cores_label') }}
            />
            {/* Response time only applies to the web dyno */}
            {isWebDyno && (
                <MetricChartWidget
                    title={t('dashboard.monitoring.chart.response_time_title')}
                    subtitle={subtitle}
                    data={responseTimePoints}
                    series={[{ key: 'response_time_avg_ms', label: t('dashboard.monitoring.chart.response_time_series'), color: 'var(--general-blue)' }]}
                    loading={loading}
                    xFormatter={formatXAxis}
                    referenceLine={{ value: RESPONSE_TIME_GUIDE_MS, label: t('dashboard.monitoring.chart.response_time_guide_label') }}
                />
            )}
        </div>
    );
};

export default DynoMonitoringSection;
